<script setup lang="ts">
import { computed, watch } from "vue";
import { useI18n } from "vue-i18n";
import { useFilterSummary } from "../../composables/Table/useFilterSummary";
import { useTableFilters } from "../../composables/Table/useTableFilters";
import VIcon from "../Icon/VIcon.vue";

defineOptions({
  name: "VTableFilterChips",
});

/**
 * The active filters as chips in the toolbar ("Status Actief"), each with a cross to remove it, and "Alles wissen".
 * The row never wraps: when the toolbar is narrow the chips scroll sideways.
 */
const { large = false } = defineProps<{
  /** Taller chips with 24px remove buttons, for phones. */
  large?: boolean
}>();

const { t } = useI18n();
const { activeFilters, getConfig, removeFilter, clearFilters, optionsOf, loadOptions } = useTableFilters();
const { summarize } = useFilterSummary();

const chips = computed(() => activeFilters.value.flatMap((filter) => {
  const config = getConfig(filter.field);
  if (!config) {
    return [];
  }
  return [{
    field: filter.field,
    label: config.label,
    value: summarize(config, filter, optionsOf(filter.field)),
  }];
}));

// A chip shows option names, so a filter set from outside the panel (a saved view) loads its options too.
watch(() => activeFilters.value.map(filter => filter.field), (fields) => {
  fields.forEach((field) => {
    void loadOptions(field).catch(() => {});
  });
}, { immediate: true });
</script>

<template>
  <div
    v-if="chips.length > 0"
    class="v-table-filter-chips flex min-w-0 flex-[0_1_auto] flex-nowrap items-center gap-1.5 overflow-x-auto"
    role="list"
    :aria-label="t('filters.active_filters')"
    data-testid="table-filter-chips"
  >
    <span
      v-for="chip in chips"
      :key="chip.field"
      role="listitem"
      class="inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-primary-100 bg-primary-50 pl-2.5 text-xs font-medium text-primary-900 dark:border-primary-800 dark:bg-primary-900/30 dark:text-primary-300"
      :class="large ? 'h-8 pr-1' : 'h-[26px] pr-1.5'"
    >
      <span class="font-normal text-gray-700 dark:text-gray-400">{{ chip.label }}</span>
      <span>{{ chip.value }}</span>
      <button
        type="button"
        class="inline-flex cursor-pointer items-center justify-center rounded-full border-none bg-transparent p-0 text-primary-900 hover:bg-primary-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-primary-300 dark:hover:bg-primary-800"
        :class="large ? 'size-6' : 'size-[18px]'"
        :aria-label="t('filters.remove_filter', { name: chip.label })"
        @click="removeFilter(chip.field)"
      >
        <VIcon name="x" class="text-[10px]" aria-hidden="true" />
      </button>
    </span>
    <span role="listitem" class="inline-flex shrink-0">
      <button
        type="button"
        class="h-7 cursor-pointer whitespace-nowrap rounded-full px-2 text-xs font-medium text-danger-700 hover:bg-gray-100 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary-500 dark:text-danger-400 dark:hover:bg-dark-700"
        data-testid="table-filter-clear-all"
        @click="clearFilters"
      >
        {{ t("filters.clear_all") }}
      </button>
    </span>
  </div>
</template>

<style scoped>
.v-table-filter-chips {
  scrollbar-width: none;
}

.v-table-filter-chips::-webkit-scrollbar {
  display: none;
}
</style>
